import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Settings, PanelLeftClose, PanelLeftOpen, Pin, PinOff, User, Building2, BadgeDollarSign, Waypoints,
  Megaphone, Workflow, FileText, Handshake, Clapperboard,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/features/command/BrandMark";
import { matchSection, useNavSections, Flyout, SECTIONS, type NavSection } from "@/components/TopNav";
import { clearRecent, togglePin, trackRecent, useQuickPlaces, type RecentItem, type RecentKind } from "@/lib/recent";

/** Sub-pages worth remembering: two clicks away otherwise. A section's own
 *  landing page is already one click away in the menu, so it isn't tracked. */
const PAGE_BY_PATH: Map<string, { label: string; icon: NavSection["icon"] }> = new Map(
  SECTIONS.flatMap((s) => (s.subnav ?? [])
    .filter((i) => i.to !== s.defaultTo && i.icon)
    .map((i) => [i.to, { label: i.label, icon: i.icon! }] as const)),
);

const KIND_ICON: Record<RecentKind, NavSection["icon"]> = {
  page: FileText, person: User, account: Building2, affiliate: BadgeDollarSign,
  partner: Handshake, creator: Clapperboard,
  source: Waypoints, campaign: Megaphone, automation: Workflow,
};
const iconFor = (it: Pick<RecentItem, "to" | "kind">) => (it.kind === "page" ? PAGE_BY_PATH.get(it.to)?.icon : undefined) ?? KIND_ICON[it.kind] ?? FileText;

const COLLAPSED_KEY = "crm-sidenav-collapsed";

/**
 * Desktop sidebar (sm+). Below sm the TopNav's icon-chip bar takes over —
 * the two are complementary halves of one navigation system, driven by the
 * same SECTIONS config. Sub-pages of the active section render as pills in
 * the TopNav toolbar, not nested here, so the sidebar stays one calm column
 * no matter how many sub-pages a section grows.
 *
 * Collapsible: the bottom toggle shrinks it to an icon rail (tooltips keep
 * items identifiable) and the choice persists per browser.
 */
export function SideNav({ workspaceName }: { workspaceName?: string }) {
  const sections = useNavSections();
  const { pathname } = useLocation();
  const activeSection = matchSection(pathname);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_KEY) === "1"
  );
  // Remember sub-pages as they're opened (records report themselves).
  useEffect(() => {
    const page = PAGE_BY_PATH.get(pathname);
    if (page) trackRecent({ to: pathname, label: page.label, kind: "page" });
  }, [pathname]);
  const toggle = () => {
    setCollapsed((c) => {
      localStorage.setItem(COLLAPSED_KEY, c ? "0" : "1");
      return !c;
    });
  };

  return (
    <aside
      className={cn(
        // No width transition: labels unmount instantly, so animating the
        // width made icons sit centered in a still-wide bar and visibly
        // slide left. An instant snap reads cleaner.
        "hidden sm:flex shrink-0 flex-col border-r border-zinc-800/60 bg-zinc-950/60 backdrop-blur-xl",
        collapsed ? "w-14" : "w-56"
      )}
    >
      {/* Logo — same row height as the top bar so the borders line up.
          Expanded: the collapse toggle sits at the row's right edge.
          Collapsed: the logo itself becomes the expand button — hovering
          swaps the bolt for a panel icon (plus a flyout label). */}
      <div
        className={cn(
          "flex h-14 shrink-0 items-center gap-2.5 border-b border-zinc-800/60",
          collapsed ? "justify-center px-0" : "px-4 pr-2"
        )}
      >
        {collapsed ? (
          <button
            type="button"
            onClick={toggle}
            aria-label="Expand menu"
            className="group relative flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-zinc-900 ring-1 ring-zinc-800 transition-colors hover:ring-indigo-400/40"
          >
            <BrandMark className="h-5 w-5 group-hover:hidden" />
            <PanelLeftOpen className="hidden h-4 w-4 text-zinc-200 group-hover:block" />
            <Flyout label="Expand menu" />
          </button>
        ) : (
          <>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-zinc-900 ring-1 ring-zinc-800">
              <BrandMark className="h-5 w-5" />
            </div>
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block truncate text-sm font-semibold tracking-tight text-zinc-50">SyncSuite</span>
              <span className="block truncate text-[10px] font-medium uppercase tracking-[0.1em] text-indigo-300/80">Command Center</span>
            </span>
            <button
              type="button"
              onClick={toggle}
              title="Collapse menu"
              aria-label="Collapse menu"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-zinc-800/40 hover:text-zinc-100"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {/* overflow-y-auto would clip the collapsed flyout labels (overflow-x
          can't be visible while overflow-y scrolls), and the icon rail is
          short enough to never need scrolling anyway. */}
      <nav className={cn("flex-1 p-2", collapsed ? "overflow-visible" : "overflow-y-auto")}>
        <div className="space-y-0.5">
          {sections.map((s) => (
            <SideItem
              key={s.defaultTo}
              to={s.defaultTo}
              end={s.defaultTo === "/"}
              label={s.label}
              icon={s.icon}
              collapsed={collapsed}
              forceActive={s === activeSection}
            />
          ))}
        </div>
        <QuickPlaces collapsed={collapsed} pathname={pathname} />
      </nav>

      <div className="border-t border-zinc-800/70 p-2">
        <SideItem to="/settings" label="Settings" icon={Settings} collapsed={collapsed} />
      </div>
    </aside>
  );
}

function SideItem({
  to, end, label, icon: Icon, collapsed, forceActive,
}: {
  to: string;
  end?: boolean;
  label: string;
  icon: NavSection["icon"];
  collapsed: boolean;
  /** Section active-state comes from matchSection (it knows about sub-page
   *  paths); plain items (Settings) fall back to NavLink's own matching. */
  forceActive?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      aria-label={label}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-[13px] font-medium transition-colors",
          collapsed && "justify-center px-0",
          (forceActive ?? isActive)
            ? "bg-indigo-500/15 text-zinc-50 ring-1 ring-inset ring-indigo-400/25"
            : "text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-100"
        )
      }
    >
      <Icon className={cn("h-4 w-4 shrink-0", (forceActive ?? false) ? "text-indigo-300" : "")} />
      {!collapsed && label}
      {collapsed && <Flyout label={label} />}
    </NavLink>
  );
}


/**
 * Pinned places (Accounts to start) and the last few opened: people,
 * accounts, affiliates, sources and sub-pages. Hover a row to pin or unpin.
 * The collapsed rail shows the pinned ones as icons.
 */
function QuickPlaces({ collapsed, pathname }: { collapsed: boolean; pathname: string }) {
  const { pinned, recent } = useQuickPlaces();
  const shownRecent = useMemo(
    () => recent.filter((r) => !pinned.some((p) => p.to === r.to)).slice(0, 6),
    [recent, pinned],
  );

  if (collapsed) {
    if (!pinned.length) return null;
    return (
      <div className="mt-3 space-y-0.5 border-t border-zinc-800/60 pt-3">
        {pinned.map((p) => (
          <SideItem key={p.to} to={p.to} label={p.label} icon={iconFor(p)} collapsed forceActive={pathname === p.to} />
        ))}
      </div>
    );
  }

  return (
    <>
      {pinned.length > 0 && (
        <div className="mt-5">
          <GroupLabel>Pinned</GroupLabel>
          <div className="space-y-0.5">
            {pinned.map((p) => <PlaceRow key={p.to} item={p} active={pathname === p.to} pinned />)}
          </div>
        </div>
      )}
      {shownRecent.length > 0 && (
        <div className="mt-5">
          <GroupLabel
            action={
              <button type="button" onClick={clearRecent} className="rounded px-1 text-[10px] font-medium normal-case tracking-normal text-zinc-600 transition-colors hover:text-zinc-300">
                Clear
              </button>
            }
          >
            Recent
          </GroupLabel>
          <div className="space-y-0.5">
            {shownRecent.map((r) => <PlaceRow key={r.to} item={r} active={pathname === r.to} />)}
          </div>
        </div>
      )}
    </>
  );
}

function GroupLabel({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="mb-1 flex items-center justify-between px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
      <span>{children}</span>
      {action}
    </div>
  );
}

function PlaceRow({ item, active, pinned }: { item: RecentItem; active: boolean; pinned?: boolean }) {
  const Icon = iconFor(item);
  return (
    <div className="group/place relative">
      <NavLink
        to={item.to}
        title={item.label}
        className={cn(
          "flex items-center gap-2.5 rounded-lg py-1.5 pl-2.5 pr-8 text-[13px] transition-colors",
          active ? "bg-zinc-800/60 text-zinc-50" : "text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-100",
        )}
      >
        <Icon className={cn("h-3.5 w-3.5 shrink-0", pinned ? "text-indigo-300" : "text-zinc-500")} />
        <span className="truncate">{item.label}</span>
      </NavLink>
      <button
        type="button"
        onClick={() => togglePin(item)}
        title={pinned ? "Unpin" : "Pin to the menu"}
        aria-label={pinned ? `Unpin ${item.label}` : `Pin ${item.label}`}
        className="absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-zinc-500 opacity-0 transition-opacity hover:bg-zinc-700/60 hover:text-zinc-100 focus-visible:opacity-100 group-hover/place:opacity-100"
      >
        {pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}
